import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, Plus, Trash2, MapPin, Image, Phone, HelpCircle, Loader2, KeyRound, Eye, EyeOff, Search, Globe, Instagram, Facebook, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettings() {
  const { token, admin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [settings, setSettings] = useState({
    hero: {
      images: [],
      overline: 'The Jewel of the Eastern Ghats',
      title: 'Experience Emerald Serenity',
      subtitle: 'Nestled amidst the majestic hills of Koraput, The Hotel Emerald invites you to a world where luxury meets the untouched beauty of the Eastern Ghats.',
      buttons: []
    },
    contact: {
      address: 'NALCO Chowk, Semiliguda, Koraput, Odisha 764036, India',
      phones: ['+91 6372 350 065', '+91 9437 082 665'],
      email: 'thehotelemerald@gmail.com',
      secondary_email: 'reservations@thehotelemerald.in',
      whatsapp: '+916372350065',
      reception_hours: '24 hours, 7 days a week'
    },
    google_maps: {
      place_id: '',
      embed_url: '',
      latitude: '18.87',
      longitude: '82.82',
      directions_url: ''
    },
    seo: {
      hotel_name: 'The Hotel Emerald',
      hotel_tagline: 'Luxury Hotel in Eastern Ghats',
      star_rating: '4',
      founding_year: '2020',
      price_range: '₹₹₹',
      instagram_url: 'https://www.instagram.com/thehotelemerald',
      facebook_url: 'https://www.facebook.com/thehotelemerald',
      tripadvisor_url: '',
      website_url: 'https://thehotelemerald.in',
      booking_url: 'https://thehotelemerald.in/book',
      languages_spoken: ['English', 'Hindi', 'Odia'],
      payment_methods: ['Cash', 'Credit Card', 'UPI'],
      check_in_time: '14:00',
      check_out_time: '11:00'
    },
    faqs: []
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/site-settings`);
      setSettings(prev => ({
        ...prev,
        ...response.data,
        hero: { ...prev.hero, ...response.data.hero },
        contact: { ...prev.contact, ...response.data.contact },
        google_maps: { ...prev.google_maps, ...response.data.google_maps },
        seo: { ...prev.seo, ...response.data.seo },
        faqs: response.data.faqs || []
      }));
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (section) => {
    setSaving(true);
    try {
      const updateData = { [section]: settings[section] };
      
      await axios.put(`${API_URL}/api/site-settings`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} settings saved!`);
    } catch (error) {
      toast.error('Failed to save settings');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'hero', label: 'Hero Section', icon: Image },
    { id: 'contact', label: 'Contact Info', icon: Phone },
    { id: 'google_maps', label: 'Google Maps', icon: MapPin },
    { id: 'seo', label: 'SEO & Social', icon: Search },
    { id: 'faqs', label: 'FAQs', icon: HelpCircle },
    { id: 'account', label: 'My Account', icon: KeyRound },
  ];

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.put(`${API_URL}/api/users/me/password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Password changed successfully');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div>
        <h1 className="text-2xl font-serif text-foreground">Site Settings</h1>
        <p className="text-muted-foreground">Manage hero section, contact info, Google Maps, and FAQs</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-border pb-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === tab.id
                ? 'bg-emerald-600 text-white'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Hero Section Tab */}
      {activeTab === 'hero' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border rounded-lg p-6 space-y-6"
        >
          <h2 className="text-lg font-semibold">Hero Section Settings</h2>
          
          {/* Hero Images */}
          <div className="space-y-3">
            <Label>Hero Images (URLs)</Label>
            {settings.hero.images.map((img, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={img}
                  onChange={(e) => {
                    const newImages = [...settings.hero.images];
                    newImages[index] = e.target.value;
                    setSettings(prev => ({
                      ...prev,
                      hero: { ...prev.hero, images: newImages }
                    }));
                  }}
                  placeholder="https://example.com/image.jpg"
                  data-testid={`hero-image-${index}`}
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    const newImages = settings.hero.images.filter((_, i) => i !== index);
                    setSettings(prev => ({
                      ...prev,
                      hero: { ...prev.hero, images: newImages }
                    }));
                  }}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              onClick={() => {
                setSettings(prev => ({
                  ...prev,
                  hero: { ...prev.hero, images: [...prev.hero.images, ''] }
                }));
              }}
              data-testid="add-hero-image-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Image
            </Button>
          </div>

          {/* Hero Text Content */}
          <div className="grid gap-4">
            <div>
              <Label htmlFor="overline">Overline Text</Label>
              <Input
                id="overline"
                value={settings.hero.overline}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  hero: { ...prev.hero, overline: e.target.value }
                }))}
                placeholder="The Jewel of the Eastern Ghats"
                data-testid="hero-overline"
              />
            </div>
            <div>
              <Label htmlFor="title">Main Title</Label>
              <Input
                id="title"
                value={settings.hero.title}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  hero: { ...prev.hero, title: e.target.value }
                }))}
                placeholder="Experience Emerald Serenity"
                data-testid="hero-title"
              />
            </div>
            <div>
              <Label htmlFor="subtitle">Subtitle</Label>
              <Textarea
                id="subtitle"
                value={settings.hero.subtitle}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  hero: { ...prev.hero, subtitle: e.target.value }
                }))}
                placeholder="Hotel description..."
                rows={3}
                data-testid="hero-subtitle"
              />
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="space-y-3">
            <Label>CTA Buttons</Label>
            {settings.hero.buttons.map((btn, index) => (
              <div key={index} className="flex gap-2 flex-wrap">
                <Input
                  value={btn.text}
                  onChange={(e) => {
                    const newButtons = [...settings.hero.buttons];
                    newButtons[index] = { ...btn, text: e.target.value };
                    setSettings(prev => ({
                      ...prev,
                      hero: { ...prev.hero, buttons: newButtons }
                    }));
                  }}
                  placeholder="Button Text"
                  className="flex-1 min-w-[150px]"
                />
                <Input
                  value={btn.link}
                  onChange={(e) => {
                    const newButtons = [...settings.hero.buttons];
                    newButtons[index] = { ...btn, link: e.target.value };
                    setSettings(prev => ({
                      ...prev,
                      hero: { ...prev.hero, buttons: newButtons }
                    }));
                  }}
                  placeholder="/book or https://..."
                  className="flex-1 min-w-[150px]"
                />
                <select
                  value={btn.style}
                  onChange={(e) => {
                    const newButtons = [...settings.hero.buttons];
                    newButtons[index] = { ...btn, style: e.target.value };
                    setSettings(prev => ({
                      ...prev,
                      hero: { ...prev.hero, buttons: newButtons }
                    }));
                  }}
                  className="px-3 py-2 border border-border rounded-md bg-background"
                >
                  <option value="primary">Primary (Emerald)</option>
                  <option value="secondary">Secondary (Gold)</option>
                </select>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    const newButtons = settings.hero.buttons.filter((_, i) => i !== index);
                    setSettings(prev => ({
                      ...prev,
                      hero: { ...prev.hero, buttons: newButtons }
                    }));
                  }}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              onClick={() => {
                setSettings(prev => ({
                  ...prev,
                  hero: {
                    ...prev.hero,
                    buttons: [...prev.hero.buttons, { text: '', link: '', style: 'primary' }]
                  }
                }));
              }}
              data-testid="add-hero-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Button
            </Button>
          </div>

          <Button onClick={() => handleSave('hero')} disabled={saving} data-testid="save-hero-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Hero Settings'}
          </Button>
        </motion.div>
      )}

      {/* Contact Info Tab */}
      {activeTab === 'contact' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border rounded-lg p-6 space-y-6"
        >
          <h2 className="text-lg font-semibold">Contact Information</h2>
          
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label htmlFor="address">Full Address</Label>
              <Textarea
                id="address"
                value={settings.contact.address}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  contact: { ...prev.contact, address: e.target.value }
                }))}
                rows={2}
                data-testid="contact-address"
              />
            </div>
            
            <div>
              <Label htmlFor="email">Primary Email</Label>
              <Input
                id="email"
                type="email"
                value={settings.contact.email}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  contact: { ...prev.contact, email: e.target.value }
                }))}
                data-testid="contact-email"
              />
            </div>
            
            <div>
              <Label htmlFor="secondary_email">Secondary Email</Label>
              <Input
                id="secondary_email"
                type="email"
                value={settings.contact.secondary_email || ''}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  contact: { ...prev.contact, secondary_email: e.target.value }
                }))}
                data-testid="contact-secondary-email"
              />
            </div>

            <div>
              <Label htmlFor="whatsapp">WhatsApp Number</Label>
              <Input
                id="whatsapp"
                value={settings.contact.whatsapp}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  contact: { ...prev.contact, whatsapp: e.target.value }
                }))}
                placeholder="+916372350065"
                data-testid="contact-whatsapp"
              />
            </div>

            <div>
              <Label htmlFor="reception_hours">Reception Hours</Label>
              <Input
                id="reception_hours"
                value={settings.contact.reception_hours}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  contact: { ...prev.contact, reception_hours: e.target.value }
                }))}
                data-testid="contact-hours"
              />
            </div>
          </div>

          {/* Phone Numbers */}
          <div className="space-y-3">
            <Label>Phone Numbers</Label>
            {settings.contact.phones.map((phone, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={phone}
                  onChange={(e) => {
                    const newPhones = [...settings.contact.phones];
                    newPhones[index] = e.target.value;
                    setSettings(prev => ({
                      ...prev,
                      contact: { ...prev.contact, phones: newPhones }
                    }));
                  }}
                  placeholder="+91 XXXX XXX XXX"
                  data-testid={`contact-phone-${index}`}
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    const newPhones = settings.contact.phones.filter((_, i) => i !== index);
                    setSettings(prev => ({
                      ...prev,
                      contact: { ...prev.contact, phones: newPhones }
                    }));
                  }}
                  disabled={settings.contact.phones.length <= 1}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              onClick={() => {
                setSettings(prev => ({
                  ...prev,
                  contact: { ...prev.contact, phones: [...prev.contact.phones, ''] }
                }));
              }}
              data-testid="add-phone-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Phone
            </Button>
          </div>

          <Button onClick={() => handleSave('contact')} disabled={saving} data-testid="save-contact-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Contact Info'}
          </Button>
        </motion.div>
      )}

      {/* Google Maps Tab */}
      {activeTab === 'google_maps' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border rounded-lg p-6 space-y-6"
        >
          <h2 className="text-lg font-semibold">Google Maps Configuration</h2>
          
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label htmlFor="place_id">Google Place ID (optional)</Label>
              <Input
                id="place_id"
                value={settings.google_maps.place_id || ''}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  google_maps: { ...prev.google_maps, place_id: e.target.value }
                }))}
                placeholder="ChIJ..."
                data-testid="maps-place-id"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Find your Place ID at: https://developers.google.com/maps/documentation/places/web-service/place-id
              </p>
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="embed_url">Google Maps Embed URL</Label>
              <Textarea
                id="embed_url"
                value={settings.google_maps.embed_url}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  google_maps: { ...prev.google_maps, embed_url: e.target.value }
                }))}
                rows={3}
                placeholder="https://www.google.com/maps/embed?pb=..."
                data-testid="maps-embed-url"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Get this from Google Maps → Share → Embed a map → Copy src URL
              </p>
            </div>

            <div>
              <Label htmlFor="latitude">Latitude</Label>
              <Input
                id="latitude"
                value={settings.google_maps.latitude}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  google_maps: { ...prev.google_maps, latitude: e.target.value }
                }))}
                placeholder="18.87"
                data-testid="maps-latitude"
              />
            </div>

            <div>
              <Label htmlFor="longitude">Longitude</Label>
              <Input
                id="longitude"
                value={settings.google_maps.longitude}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  google_maps: { ...prev.google_maps, longitude: e.target.value }
                }))}
                placeholder="82.82"
                data-testid="maps-longitude"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="directions_url">Directions URL</Label>
              <Input
                id="directions_url"
                value={settings.google_maps.directions_url}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  google_maps: { ...prev.google_maps, directions_url: e.target.value }
                }))}
                placeholder="https://www.google.com/maps/dir/?api=1&destination=..."
                data-testid="maps-directions-url"
              />
            </div>
          </div>

          {/* Map Preview */}
          {settings.google_maps.embed_url && (
            <div>
              <Label>Map Preview</Label>
              <div className="mt-2 rounded-lg overflow-hidden border border-border h-[300px]">
                <iframe
                  src={settings.google_maps.embed_url}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Map Preview"
                />
              </div>
            </div>
          )}

          <Button onClick={() => handleSave('google_maps')} disabled={saving} data-testid="save-maps-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Maps Settings'}
          </Button>
        </motion.div>
      )}

      {/* FAQs Tab */}
      {activeTab === 'faqs' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border rounded-lg p-6 space-y-6"
        >
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold">FAQs (For Voice & AI SEO)</h2>
              <p className="text-sm text-muted-foreground">
                These FAQs will be used for voice search optimization and AI assistants
              </p>
            </div>
            <Button
              onClick={() => {
                setSettings(prev => ({
                  ...prev,
                  faqs: [...prev.faqs, { question: '', answer: '', category: 'general' }]
                }));
              }}
              data-testid="add-faq-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add FAQ
            </Button>
          </div>

          <div className="space-y-4">
            {settings.faqs.map((faq, index) => (
              <div key={index} className="bg-muted/30 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 space-y-3">
                    <div>
                      <Label>Question</Label>
                      <Input
                        value={faq.question}
                        onChange={(e) => {
                          const newFaqs = [...settings.faqs];
                          newFaqs[index] = { ...faq, question: e.target.value };
                          setSettings(prev => ({ ...prev, faqs: newFaqs }));
                        }}
                        placeholder="What is your check-in time?"
                        data-testid={`faq-question-${index}`}
                      />
                    </div>
                    <div>
                      <Label>Answer</Label>
                      <Textarea
                        value={faq.answer}
                        onChange={(e) => {
                          const newFaqs = [...settings.faqs];
                          newFaqs[index] = { ...faq, answer: e.target.value };
                          setSettings(prev => ({ ...prev, faqs: newFaqs }));
                        }}
                        placeholder="Our check-in time is 2:00 PM..."
                        rows={2}
                        data-testid={`faq-answer-${index}`}
                      />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <select
                        value={faq.category}
                        onChange={(e) => {
                          const newFaqs = [...settings.faqs];
                          newFaqs[index] = { ...faq, category: e.target.value };
                          setSettings(prev => ({ ...prev, faqs: newFaqs }));
                        }}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background"
                        data-testid={`faq-category-${index}`}
                      >
                        <option value="general">General</option>
                        <option value="rooms">Rooms & Booking</option>
                        <option value="dining">Dining</option>
                        <option value="amenities">Amenities</option>
                        <option value="location">Location & Transport</option>
                      </select>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const newFaqs = settings.faqs.filter((_, i) => i !== index);
                      setSettings(prev => ({ ...prev, faqs: newFaqs }));
                    }}
                    data-testid={`delete-faq-${index}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}

            {settings.faqs.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <HelpCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No FAQs added yet. Click "Add FAQ" to create your first FAQ.</p>
              </div>
            )}
          </div>

          <Button onClick={() => handleSave('faqs')} disabled={saving} data-testid="save-faqs-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save FAQs'}
          </Button>
        </motion.div>
      )}

      {/* SEO & Social Tab */}
      {activeTab === 'seo' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Hotel Identity */}
          <div className="bg-card border border-border rounded-lg p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-5 h-5 text-emerald-600" />
              <h2 className="text-lg font-semibold">Hotel Identity</h2>
            </div>
            
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="hotel_name">Hotel Name</Label>
                <Input
                  id="hotel_name"
                  value={settings.seo.hotel_name}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    seo: { ...prev.seo, hotel_name: e.target.value }
                  }))}
                  placeholder="The Hotel Emerald"
                  data-testid="seo-hotel-name"
                />
              </div>
              <div>
                <Label htmlFor="hotel_tagline">Tagline</Label>
                <Input
                  id="hotel_tagline"
                  value={settings.seo.hotel_tagline}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    seo: { ...prev.seo, hotel_tagline: e.target.value }
                  }))}
                  placeholder="Luxury Hotel in Eastern Ghats"
                  data-testid="seo-tagline"
                />
              </div>
              <div>
                <Label htmlFor="star_rating">Star Rating</Label>
                <select
                  id="star_rating"
                  value={settings.seo.star_rating}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    seo: { ...prev.seo, star_rating: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background"
                  data-testid="seo-star-rating"
                >
                  <option value="3">3 Star</option>
                  <option value="4">4 Star</option>
                  <option value="5">5 Star</option>
                </select>
              </div>
              <div>
                <Label htmlFor="founding_year">Founding Year</Label>
                <Input
                  id="founding_year"
                  value={settings.seo.founding_year}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    seo: { ...prev.seo, founding_year: e.target.value }
                  }))}
                  placeholder="2020"
                  data-testid="seo-founding-year"
                />
              </div>
              <div>
                <Label htmlFor="price_range">Price Range</Label>
                <select
                  id="price_range"
                  value={settings.seo.price_range}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    seo: { ...prev.seo, price_range: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background"
                  data-testid="seo-price-range"
                >
                  <option value="₹">₹ (Budget)</option>
                  <option value="₹₹">₹₹ (Moderate)</option>
                  <option value="₹₹₹">₹₹₹ (Premium)</option>
                  <option value="₹₹₹₹">₹₹₹₹ (Luxury)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Check-in/Check-out Times */}
          <div className="bg-card border border-border rounded-lg p-6 space-y-4">
            <h2 className="text-lg font-semibold">Check-in / Check-out Times</h2>
            <p className="text-sm text-muted-foreground">These times appear in search results and voice assistant responses</p>
            
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="check_in_time">Check-in Time</Label>
                <Input
                  id="check_in_time"
                  type="time"
                  value={settings.seo.check_in_time}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    seo: { ...prev.seo, check_in_time: e.target.value }
                  }))}
                  data-testid="seo-checkin-time"
                />
              </div>
              <div>
                <Label htmlFor="check_out_time">Check-out Time</Label>
                <Input
                  id="check_out_time"
                  type="time"
                  value={settings.seo.check_out_time}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    seo: { ...prev.seo, check_out_time: e.target.value }
                  }))}
                  data-testid="seo-checkout-time"
                />
              </div>
            </div>
          </div>

          {/* Social Media Links */}
          <div className="bg-card border border-border rounded-lg p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Instagram className="w-5 h-5 text-pink-600" />
              <h2 className="text-lg font-semibold">Social Media Links</h2>
            </div>
            
            <div className="grid gap-4">
              <div>
                <Label htmlFor="instagram_url">Instagram URL</Label>
                <Input
                  id="instagram_url"
                  value={settings.seo.instagram_url}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    seo: { ...prev.seo, instagram_url: e.target.value }
                  }))}
                  placeholder="https://www.instagram.com/yourhotel"
                  data-testid="seo-instagram"
                />
              </div>
              <div>
                <Label htmlFor="facebook_url">Facebook URL</Label>
                <Input
                  id="facebook_url"
                  value={settings.seo.facebook_url}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    seo: { ...prev.seo, facebook_url: e.target.value }
                  }))}
                  placeholder="https://www.facebook.com/yourhotel"
                  data-testid="seo-facebook"
                />
              </div>
              <div>
                <Label htmlFor="tripadvisor_url">TripAdvisor URL</Label>
                <Input
                  id="tripadvisor_url"
                  value={settings.seo.tripadvisor_url}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    seo: { ...prev.seo, tripadvisor_url: e.target.value }
                  }))}
                  placeholder="https://www.tripadvisor.com/Hotel_Review-yourhotel"
                  data-testid="seo-tripadvisor"
                />
              </div>
            </div>
          </div>

          {/* Website URLs */}
          <div className="bg-card border border-border rounded-lg p-6 space-y-4">
            <h2 className="text-lg font-semibold">Website URLs</h2>
            
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="website_url">Main Website URL</Label>
                <Input
                  id="website_url"
                  value={settings.seo.website_url}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    seo: { ...prev.seo, website_url: e.target.value }
                  }))}
                  placeholder="https://thehotelemerald.in"
                  data-testid="seo-website-url"
                />
              </div>
              <div>
                <Label htmlFor="booking_url">Booking URL</Label>
                <Input
                  id="booking_url"
                  value={settings.seo.booking_url}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    seo: { ...prev.seo, booking_url: e.target.value }
                  }))}
                  placeholder="https://thehotelemerald.in/book"
                  data-testid="seo-booking-url"
                />
              </div>
            </div>
          </div>

          {/* Languages & Payment */}
          <div className="bg-card border border-border rounded-lg p-6 space-y-4">
            <h2 className="text-lg font-semibold">Languages & Payment Methods</h2>
            
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Languages Spoken (comma-separated)</Label>
                <Input
                  value={settings.seo.languages_spoken?.join(', ') || ''}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    seo: { ...prev.seo, languages_spoken: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }
                  }))}
                  placeholder="English, Hindi, Odia"
                  data-testid="seo-languages"
                />
              </div>
              <div>
                <Label>Payment Methods (comma-separated)</Label>
                <Input
                  value={settings.seo.payment_methods?.join(', ') || ''}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    seo: { ...prev.seo, payment_methods: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }
                  }))}
                  placeholder="Cash, Credit Card, UPI"
                  data-testid="seo-payments"
                />
              </div>
            </div>
          </div>

          <Button onClick={() => handleSave('seo')} disabled={saving} data-testid="save-seo-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save SEO Settings'}
          </Button>
        </motion.div>
      )}

      {/* Account Tab - Change Password */}
      {activeTab === 'account' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* User Info Card */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Account Information</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label className="text-muted-foreground">Username</Label>
                <p className="font-medium">{admin?.username || 'admin'}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Email</Label>
                <p className="font-medium">{admin?.email || 'admin@thehotelemerald.in'}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Full Name</Label>
                <p className="font-medium">{admin?.full_name || 'Administrator'}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Role</Label>
                <p className="font-medium capitalize">{admin?.role || 'admin'}</p>
              </div>
            </div>
          </div>

          {/* Change Password Card */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Change Password</h2>
            <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
              <div>
                <Label htmlFor="current_password">Current Password</Label>
                <div className="relative">
                  <Input
                    id="current_password"
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                    required
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                  >
                    {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="new_password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new_password"
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                    required
                    minLength={8}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                  >
                    {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Minimum 8 characters</p>
              </div>

              <div>
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirm_password"
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                    required
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                  >
                    {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" disabled={changingPassword} data-testid="change-password-btn">
                <KeyRound className="w-4 h-4 mr-2" />
                {changingPassword ? 'Changing...' : 'Change Password'}
              </Button>
            </form>
          </div>
        </motion.div>
      )}
    </div>
  );
}
